import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Gavel, ExternalLink, ChevronUp, ChevronDown, ChevronsUpDown, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { formatAmount } from "@/utils/formatAmount";
import { Badge } from "@/components/ui/Badge";
import { PageLoader } from "@/components/ui/Spinner";
import { RangeFilter } from "@/components/ui/RangeFilter";
import { useTenders, usePendingApprovalsCount, useTenderActionCounts } from "../hooks/useTenders";
import { useTenderListLive } from "../hooks/useTenderListLive";
import {
  ACTION_STATUS_COLORS,
  tenderActionLabel,
  TENDER_STATUS_COLORS,
  TENDER_STATUS_LABELS,
  tenderActionSortKey,
  WORK_SCOPE_LABEL,
  type TenderStatus,
  type TenderWorkScope,
} from "../types";

/** Krótki znacznik zakresu przy tytule. Projekt zostaje bez chipa — to 100% dzisiejszej
 *  bazy, więc oznaczanie go byłoby szumem; wyróżniamy to, co nowe albo wymaga uwagi. */
function ScopeChip({ workScope }: { workScope: TenderWorkScope }) {
  if (workScope === "projekt") return null;
  const cls = !workScope
    ? "bg-red-50 text-red-700 border-red-200"
    : workScope === "wykonawstwo"
      ? "bg-orange-50 text-orange-700 border-orange-200"
      : "bg-purple-50 text-purple-700 border-purple-200";
  return (
    <span
      className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 font-mono text-[10px] uppercase ${cls}`}
      title={workScope ? WORK_SCOPE_LABEL[workScope] : "Nie udało się odczytać zakresu z nazwy"}
    >
      {!workScope ? "zakres?" : workScope === "wykonawstwo" ? "budowa" : "proj+bud"}
    </span>
  );
}

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Wszystkie" },
  { value: "open", label: "Otwarte" },
  { value: "won_unofficial", label: "Wygrany - nieoficjalnie" },
  { value: "won", label: "Wygrane" },
  { value: "lost", label: "Przegrane" },
  { value: "cancelled", label: "Anulowane" },
  // Soft-deleted tenders are hidden from "Wszystkie"; this filter surfaces them.
  { value: "not_starting", label: "Nie startujemy" },
];

function BadgeDot({ color }: { color: "amber" | "red" | "brand" | "rose" | "violet" }) {
  const cls =
    color === "amber"
      ? "bg-amber-400"
      : color === "red"
        ? "bg-red-500"
        : color === "rose"
          ? "bg-rose-500"
          : color === "violet"
            ? "bg-violet-500"
            : "bg-[var(--color-primary)]";
  return <span className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${cls}`} />;
}

type ActionFilter = "needs_approval" | "needs_offer" | "needs_pricing" | "pricing_claimed" | "needs_result" | null;

function TendersBadgeSummary({
  activeFilter,
  onFilter,
}: {
  activeFilter: ActionFilter;
  onFilter: (f: ActionFilter) => void;
}) {
  const user = useAuthStore((s) => s.user);
  const canApprove = !!user?.has_approve_pricing_permission;
  const canIssueOffers = !!user?.has_issue_offers_permission;
  const canCalculate = !!user?.has_calculate_pricing_permission;

  const { data: pendingApprovals = 0 } = usePendingApprovalsCount(canApprove);
  const { data: actionCounts } = useTenderActionCounts(
    canIssueOffers || canCalculate,
  );

  const items: Array<{
    color: "amber" | "red" | "brand" | "rose" | "violet";
    count: number;
    label: string;
    filter: ActionFilter;
  }> = [];

  if (canApprove)
    items.push({ color: "amber", count: pendingApprovals, label: "oczekuje na zatwierdzenie wyceny", filter: "needs_approval" });

  if (canIssueOffers)
    items.push({ color: "red", count: actionCounts?.needs_offer ?? 0, label: "wymaga wystawienia oferty", filter: "needs_offer" });

  if (canCalculate) {
    items.push({ color: "brand", count: actionCounts?.needs_pricing ?? 0, label: "wymaga wyceny", filter: "needs_pricing" });
    items.push({ color: "violet", count: actionCounts?.pricing_claimed ?? 0, label: "przejęte — ktoś liczy wycenę", filter: "pricing_claimed" });
  }

  if (canIssueOffers)
    items.push({ color: "rose", count: actionCounts?.needs_result ?? 0, label: "oferta złożona — minęło 21 dni bez wyniku", filter: "needs_result" });

  if (items.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-3 mb-5">
      {items.map((item) => {
        const isActive = activeFilter === item.filter;
        return (
          <button
            key={item.label}
            onClick={() => onFilter(isActive ? null : item.filter)}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors cursor-pointer ${
              isActive
                ? "bg-gray-200 dark:bg-gray-600 border-gray-400 dark:border-gray-400"
                : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            <BadgeDot color={item.color} />
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {item.count}
            </span>
            <span className="text-gray-500 dark:text-gray-400">
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

const PAGE_SIZE = 40;

type SortKey = "title" | "client_name" | "offer_deadline" | "rounds_count" | "latest_offer_value" | "status" | "action_status";
type SortDir = "asc" | "desc";

export function TendersListPage() {
  const user = useAuthStore((s) => s.user);
  const canCreate = user?.role === "director" || user?.role === "coordinator";
  const [statusFilter, setStatusFilter] = useState("");
  const [actionFilter, setActionFilter] = useState<ActionFilter>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  // Zakres terminu oferty ("YYYY-MM-DD", pusty = brak ograniczenia z tej strony).
  const [deadlineFrom, setDeadlineFrom] = useState("");
  const [deadlineTo, setDeadlineTo] = useState("");
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(1);

  const { data: rawTenders = [], isLoading } = useTenders(
    statusFilter ? { status: statusFilter } : undefined
  );
  // Zmiany statusów „na żywo" — tylko póki ta strona jest otwarta (zob. hook).
  useTenderListLive();

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1);
  }

  // Granice terminu liczone lokalnie: „od" obejmuje cały dzień od północy, „do" — do
  // końca wybranego dnia, żeby przetarg z terminem 15.09 12:00 wpadł w 15.09–15.09.
  const deadlineFromDate = deadlineFrom ? new Date(`${deadlineFrom}T00:00:00`) : null;
  const deadlineToDate = deadlineTo ? new Date(`${deadlineTo}T23:59:59.999`) : null;

  const filteredTenders = [...rawTenders]
    .sort((a, b) => {
      if (sortKey) {
        const dir = sortDir === "asc" ? 1 : -1;
        switch (sortKey) {
          case "title":
            return dir * a.title.localeCompare(b.title, "pl");
          case "client_name":
            return dir * (a.client_name ?? "").localeCompare(b.client_name ?? "", "pl");
          case "offer_deadline":
            return dir * (new Date(a.offer_deadline).getTime() - new Date(b.offer_deadline).getTime());
          case "rounds_count":
            return dir * (a.rounds_count - b.rounds_count);
          case "latest_offer_value":
            return dir * ((parseFloat(a.latest_offer_value ?? "0")) - (parseFloat(b.latest_offer_value ?? "0")));
          case "status":
            return dir * a.status.localeCompare(b.status, "pl");
          case "action_status":
            return dir * (tenderActionSortKey(a.action_status) - tenderActionSortKey(b.action_status));
        }
      }
      // default: action_status priority, then offer_deadline asc
      const aKey = tenderActionSortKey(a.action_status);
      const bKey = tenderActionSortKey(b.action_status);
      if (aKey !== bKey) return aKey - bKey;
      return new Date(a.offer_deadline).getTime() - new Date(b.offer_deadline).getTime();
    })
    .filter((t) => !actionFilter || t.action_status === actionFilter)
    .filter((t) =>
      !scopeFilter ||
      (scopeFilter === "__none__" ? !t.work_scope : t.work_scope === scopeFilter)
    )
    .filter((t) => {
      if (!deadlineFromDate && !deadlineToDate) return true;
      // Przetarg bez terminu wypada, gdy pytamy o przedział — nie da się orzec,
      // że się w nim mieści.
      if (!t.offer_deadline) return false;
      const d = new Date(t.offer_deadline);
      if (deadlineFromDate && d < deadlineFromDate) return false;
      if (deadlineToDate && d > deadlineToDate) return false;
      return true;
    })
    .filter((t) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        t.title.toLowerCase().includes(q) ||
        (t.reference_number?.toLowerCase().includes(q) ?? false)
      );
    });

  const totalPages = Math.max(1, Math.ceil(filteredTenders.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const tenders = filteredTenders.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function handleStatusFilter(value: string) {
    setStatusFilter(value);
    setActionFilter(null);
    setPage(1);
  }

  function handleActionFilter(f: ActionFilter) {
    setActionFilter(f);
    if (f) setStatusFilter("open");
    setPage(1);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Przetargi</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
        </div>
        {canCreate && (
          <Link
            to="/tenders/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary-hover)]"
          >
            <Plus size={14} /> Nowy przetarg
          </Link>
        )}
      </div>

      <TendersBadgeSummary activeFilter={actionFilter} onFilter={handleActionFilter} />

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => handleStatusFilter(opt.value)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors border ${
              statusFilter === opt.value && !actionFilter
                ? "bg-gray-200 dark:bg-gray-600 border-gray-400 dark:border-gray-400 text-gray-900 dark:text-gray-100"
                : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            {opt.label}
          </button>
        ))}
        {/* Zakres — „nieustalony" jest osobnym kubełkiem, bo to lista do uzupełnienia,
            a nie brak danych do zignorowania. */}
        <select
          value={scopeFilter}
          onChange={(e) => { setScopeFilter(e.target.value); setPage(1); }}
          className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300"
        >
          <option value="">Zakres: wszystkie</option>
          {(Object.entries(WORK_SCOPE_LABEL) as [string, string][]).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
          <option value="__none__">Zakres: nieustalony</option>
        </select>
        <RangeFilter
          label="Termin"
          type="date"
          from={deadlineFrom}
          to={deadlineTo}
          onFrom={(v) => { setDeadlineFrom(v); setPage(1); }}
          onTo={(v) => { setDeadlineTo(v); setPage(1); }}
          fromTitle="Termin oferty od"
          toTitle="Termin oferty do"
          idleBorderClassName="border-gray-300 dark:border-gray-600"
          inputClassName="px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300 min-w-[130px]"
        />
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            placeholder="Szukaj po nazwie lub numerze..."
            className="pl-8 pr-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] w-52"
          />
        </div>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : tenders.length === 0 ? (
        <div className="text-center py-16 text-gray-400 dark:text-gray-500">
          <Gavel size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Brak przetargów.</p>
        </div>
      ) : (
        <>
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {tenders.map((t) => (
              <div key={t.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Link to={`/tenders/${t.id}`} className="font-medium text-[var(--color-primary)] hover:underline">
                        {t.title}
                      </Link>
                      <ScopeChip workScope={t.work_scope} />
                      {t.source_url && (
                        <a href={t.source_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[var(--color-primary)]">
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                    {t.reference_number && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 font-mono mt-0.5">{t.reference_number}</p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {t.client_name}{t.client_branch_name ? ` · ${t.client_branch_name}` : ""}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      Termin: {new Date(t.offer_deadline).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <Badge color={TENDER_STATUS_COLORS[t.status as TenderStatus]}>
                      {TENDER_STATUS_LABELS[t.status as TenderStatus]}
                    </Badge>
                    {t.action_status && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_STATUS_COLORS[t.action_status]}`}>
                        {tenderActionLabel(t.action_status, t.pricing_claimed_by_name)}
                      </span>
                    )}
                    {(t.latest_offer_value || t.contract_value) && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        {formatAmount((t.latest_offer_value ?? t.contract_value) as string)} PLN
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[700px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  {(
                    [
                      { key: "title", label: "Tytuł", align: "left" },
                      { key: "client_name", label: "Klient", align: "left" },
                      { key: "offer_deadline", label: "Termin oferty", align: "left" },
                      { key: "rounds_count", label: "Tury", align: "left" },
                      { key: "latest_offer_value", label: "Ostatnia oferta", align: "left" },
                      { key: "status", label: "Status", align: "left" },
                      { key: "action_status", label: "Do zrobienia", align: "center" },
                    ] as Array<{ key: SortKey; label: string; align: "left" | "center" }>
                  ).map(({ key, label, align }) => {
                    const active = sortKey === key;
                    const Icon = active ? (sortDir === "asc" ? ChevronUp : ChevronDown) : ChevronsUpDown;
                    return (
                      <th
                        key={key}
                        onClick={() => handleSort(key)}
                        className={`px-4 py-2 font-mono text-xs uppercase tracking-widest cursor-pointer select-none transition-colors hover:text-gray-800 dark:hover:text-gray-200 ${align === "center" ? "text-center" : "text-left"} ${active ? "text-gray-800 dark:text-gray-200" : "text-gray-500 dark:text-gray-400"}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {label}
                          <Icon size={12} className={active ? "opacity-100" : "opacity-40"} />
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {tenders.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Link to={`/tenders/${t.id}`} className="font-medium text-[var(--color-primary)] hover:underline">{t.title}</Link>
                        <ScopeChip workScope={t.work_scope} />
                        {t.source_url && (
                          <a href={t.source_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[var(--color-primary)]" title="Otwórz ogłoszenie">
                            <ExternalLink size={12} />
                          </a>
                        )}
                      </div>
                      {t.reference_number && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">{t.reference_number}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      <span>{t.client_name}</span>
                      {t.client_branch_name && (
                        <span className="block text-xs text-[var(--color-primary)] dark:text-blue-400">{t.client_branch_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">
                      {new Date(t.offer_deadline).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">{t.rounds_count}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-mono text-xs">
                      {t.latest_offer_value ? (
                        `${formatAmount(t.latest_offer_value)} PLN`
                      ) : t.latest_pricing_value ? (
                        <span
                          className="text-gray-400 dark:text-gray-500"
                          title="Najnowszy wariant wyceny — brak złożonej oferty"
                        >
                          {formatAmount(t.latest_pricing_value)} PLN
                        </span>
                      ) : t.contract_value ? (
                        <span title="Wartość umowy — wygrana bez ofert w turach">
                          {formatAmount(t.contract_value)} PLN
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge color={TENDER_STATUS_COLORS[t.status as TenderStatus]}>{TENDER_STATUS_LABELS[t.status as TenderStatus]}</Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {t.action_status ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_STATUS_COLORS[t.action_status]}`}>
                          {tenderActionLabel(t.action_status, t.pricing_claimed_by_name)}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Strona {safePage} z {totalPages} ({filteredTenders.length} przetargów)
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="p-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={14} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
                  .reduce<Array<number | "ellipsis">>((acc, p, idx, arr) => {
                    if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("ellipsis");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((item, idx) =>
                    item === "ellipsis" ? (
                      <span key={`e${idx}`} className="px-1.5 text-xs text-gray-400">…</span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setPage(item)}
                        className={`min-w-[28px] px-2 py-1 rounded border text-xs transition-colors ${
                          item === safePage
                            ? "bg-[var(--color-primary)] border-[var(--color-primary)] text-white"
                            : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                        }`}
                      >
                        {item}
                      </button>
                    )
                  )}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="p-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
